import { Button, Input, Modal, Form, Radio } from 'antd';
import React, { FC, useContext, useEffect } from 'react';
import { Hitch } from 'services/apps/hitches';
import { getInterestState } from './utils';
import { FormattedMessage, useIntl } from 'react-intl';
import { UserAuthenticationContext } from 'components/context/UserAuthenticationProvider';
import * as Sentry from '@sentry/node';
import { createSubscription } from 'services/public/forms';
import { useRouter } from 'next/router';

const FormItem = Form.Item;
const { TextArea } = Input;

interface SubscribeFormProps {
  firstApp?: Hitch;
  secondApp?: Hitch;
  onSubmit?: () => void;
}

const SubscribeForm: FC<SubscribeFormProps> = (props) => {
  const { formatMessage } = useIntl();
  const { push, query } = useRouter();

  const { firstApp, secondApp, onSubmit } = props;
  const { user } = useContext(UserAuthenticationContext);
  const interestState = getInterestState(firstApp, secondApp);

  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldsValue({
      about: `${firstApp ? firstApp.id : ''} ${secondApp?.id ? `- ${secondApp.id}` : ''}`,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstApp, secondApp, form]);

  const formItemLayout = {
    labelCol: {
      sm: { span: 24 },
      md: { span: 9 },
      xl: { span: 7 },
    },
    wrapperCol: {
      sm: { span: 24 },
      md: { span: 13 },
      xl: { span: 10 },
    },
  };

  const submitFormLayout = {
    wrapperCol: {
      xs: { span: 24, offset: 0 },
      md: { span: 10, offset: 7 },
    },
  };

  const onFinish = async (values: { [key: string]: any }) => {
    try {
      await createSubscription(
        {
          ...values,
          hitches: [
            ...(firstApp?.id ? [firstApp?.name] : []),
            ...(secondApp?.id ? [secondApp?.name] : []),
          ],
        },
        user
      );
      push({ query: { ...query, subscribed: undefined } });
      if (onSubmit) {
        onSubmit();
      }
      Modal.success({
        content: 'Thanks for subscribed us',
      });
    } catch (e) {
      Modal.error({
        content:
          'Unfortunately we were unable to process this form. Please try again, or send us an email.',
      });
      Sentry.captureException(`Unable to save form with data ${JSON.stringify(values)}`);
    }
  };

  const onFinishFailed = (errorInfo: unknown) => {
    throw new Error(errorInfo as string);
  };

  return (
    <>
      <Form
        hideRequiredMark
        style={{ marginTop: 8 }}
        form={form}
        name="basic"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
      >
        <FormItem
          {...formItemLayout}
          label={formatMessage({
            defaultMessage: 'About',
            description: 'subscribe-form/about/label',
          })}
          name="about"
          rules={[
            {
              required: true,
              message: formatMessage({
                defaultMessage: 'About field is required',
                description: 'subscribe-form/about/required',
              }),
            },
          ]}
        >
          <Input placeholder={firstApp ? firstApp.id : ''} />
        </FormItem>
        <FormItem
          {...formItemLayout}
          label={formatMessage({
            defaultMessage: 'Email',
            description: 'subscribe-form/email/label',
          })}
          name="email"
          rules={[
            {
              type: 'email',
              message: formatMessage({
                defaultMessage: 'Please, leave a valid email address',
                description: 'subscribe-form/email/valid',
              }),
            },
            {
              required: true,
              message: formatMessage({
                defaultMessage: 'Email is required',
                description: 'subscribe-form/email/required',
              }),
            },
          ]}
        >
          <Input placeholder={'your@mail.com'} />
        </FormItem>
        {(interestState.state === 'one-app-supported' ||
          interestState.state === 'one-app-not-supported') && (
          <>
            <FormItem
              {...formItemLayout}
              label={formatMessage({
                defaultMessage: 'App you want to integrate with',
                description: 'subscribe-form/custom-app/label',
              })}
              name="custom-app"
            >
              <Input
                placeholder={formatMessage({
                  defaultMessage: 'fill in the name of the software package',
                  description: 'subscribe-form/custom-app/placeholder',
                })}
              />
            </FormItem>
            <FormItem
              {...formItemLayout}
              label={formatMessage({
                defaultMessage: 'Website of that app',
                description: 'subscribe-form/website/label',
              })}
              name="custom-app-website"
              rules={[
                {
                  type: 'url',
                  message: formatMessage({
                    defaultMessage: 'please provide a valid url, with http://',
                    description: 'subscribe-form/website/valid',
                  }),
                },
              ]}
            >
              <Input
                placeholder={formatMessage({
                  defaultMessage: 'Specify the website url',
                  description: 'subscribe-form/website/placeholder',
                })}
              />
            </FormItem>
            <FormItem
              {...formItemLayout}
              label={formatMessage({
                defaultMessage: 'That app has an API',
                description: 'subscribe-form/api/label',
              })}
              name="api"
            >
              <div>
                <Radio.Group>
                  <Radio value="yes">
                    {formatMessage({
                      defaultMessage: 'Yes',
                      description: 'subscribe-form/api/label/value1',
                    })}
                  </Radio>
                  <Radio value="no">
                    {formatMessage({
                      defaultMessage: 'No',
                      description: 'subscribe-form/api/label/value2',
                    })}
                  </Radio>
                  <Radio value="dontknow">
                    {formatMessage({
                      defaultMessage: "I don't know",
                      description: 'subscribe-form/api/label/value3',
                    })}
                  </Radio>
                </Radio.Group>
              </div>
            </FormItem>
          </>
        )}

        <FormItem
          {...formItemLayout}
          label={formatMessage({
            defaultMessage: 'Tell us what you want to achieve',
            description: 'subscribe-form/goal/label',
          })}
          name="goal"
        >
          <TextArea
            style={{ minHeight: 32 }}
            placeholder={formatMessage({
              defaultMessage: 'Let yourself go, the more we know, the better we can help you.',
              description: 'subscribe-form/goal/placeholder',
            })}
            rows={8}
          />
        </FormItem>

        <FormItem
          {...formItemLayout}
          label={formatMessage({
            defaultMessage: 'Stay up to date',
            description: 'subscribe-form/up-to-date/label',
          })}
          name="subscribeType"
        >
          <div>
            <Radio.Group>
              <Radio value="specific">
                {formatMessage({
                  defaultMessage: 'Only concerning my case',
                  description: 'subscribe-form/up-to-date/option1',
                })}
              </Radio>
              <Radio value="all">
                {formatMessage({
                  defaultMessage: 'All updates on the platform',
                  description: 'subscribe-form/up-to-date/option2',
                })}
              </Radio>
            </Radio.Group>
          </div>
        </FormItem>

        <FormItem {...submitFormLayout} style={{ marginTop: 32 }}>
          <Button type="primary" htmlType="submit">
            <FormattedMessage defaultMessage="Submit" description="subscribe-form/submit" />
          </Button>
        </FormItem>
      </Form>
    </>
  );
};

export default SubscribeForm;
